import React, { useState, useEffect, useRef } from "react";
import { Search, Bell, X, CheckCheck, Briefcase, Wallet, UserMinus, FileText, Calendar, PackageCheck, HelpCircle, UserCog, Award, Maximize2, Inbox, PartyPopper } from "lucide-react";
import api from "../api";
import { K } from "../theme";
import { Av, Inp, Sl, Bt, Bg } from "../components";

// Icon + tint per notification category so the tray reads at a glance
// instead of a wall of text.
const CATEGORY_META = {
  recruitment:  { Icon: Briefcase,   c: K.p },
  interview:    { Icon: Calendar,    c: K.bl },
  deduction:    { Icon: Wallet,      c: K.r },
  vendor_bill:  { Icon: FileText,    c: K.a },
  expense:      { Icon: Wallet,      c: K.a },
  separation:   { Icon: UserMinus,   c: K.r },
  onboarding:   { Icon: PackageCheck, c: K.g },
  intake:       { Icon: FileText,    c: K.bl },
  leave:        { Icon: Calendar,    c: K.bl },
  asset:        { Icon: PackageCheck, c: K.p },
  helpdesk:     { Icon: HelpCircle,  c: K.a },
  shift:        { Icon: Calendar,    c: K.pu },
  appraisal:    { Icon: Award,       c: K.pu },
  celebration:  { Icon: PartyPopper, c: K.ro },
  system:       { Icon: Bell,        c: K.t3 },
};
const iconFor = (cat) => CATEGORY_META[cat] || { Icon: Bell, c: K.t3 };
// Friendly label per category, for the full-window notification center's
// Gmail-style label rail. Falls back to title-casing the raw category for
// any backend category not listed here, so a new one never renders blank.
const CATEGORY_LABEL = {
  recruitment: 'Recruitment', interview: 'Interviews', deduction: 'Deductions',
  vendor_bill: 'Vendor Bills', expense: 'Expenses', separation: 'Offboarding',
  onboarding: 'Onboarding', intake: 'Intake', leave: 'Leaves', asset: 'Assets',
  helpdesk: 'Helpdesk', shift: 'Shift Requests', appraisal: 'Appraisals', celebration: 'Celebrations', system: 'System',
  probation_evaluation: 'Probation Evals', training_review: 'Training Reviews',
};
const labelFor = (cat) => CATEGORY_LABEL[cat] || (cat ? cat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Other');
// "just now"/"5m" are complete; "5m" needs " ago" suffix. Handle both.
const fmtTime = (t) => {
  const s = String(t || '').trim();
  if (!s) return '';
  if (s === 'just now') return s;
  return s + ' ago';
};

// Detail row in the employee quick-view card.
const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 py-[9px] border-b border-b0 last:border-0">
    <span className="text-[14px] text-t3 uppercase tracking-wide flex-shrink-0">{label}</span>
    <span className="text-[14px] font-semibold text-t1 text-right break-all">{value || "—"}</span>
  </div>
);

// Window shown when an employee is picked from the global search. Basic
// directory details only — Name + ID in the banner, the rest as rows.
// Employees.jsx's own directory (Basic Info/Employment/Salary tabs, edit
// access, etc.) is gated to these roles — same list the sidebar NAV uses
// for the "Employees" item. Offering "Go to Profile" to anyone else would
// link to a page they can't open.
const CAN_OPEN_PROFILE = ["admin", "hr_head", "centre_hr", "centre_head", "recruiter", "accounts"];

// One notification row — shared by the small header dropdown and the
// full-window Notification Center so the two never drift out of sync.
// `dense` shrinks the icon/padding slightly for the dropdown's narrower list.
const NotificationRow = ({ n, onNotifClick, onQuickApprove, onLeaveAction, dense }) => {
  const { Icon, c } = iconFor(n.category);
  const isRead = n.is_read || n.rd;
  const clickable = !!n.link || !isRead;
  // Asset requests get an inline one-click "Approve" — the backend decides
  // whether this notification's stage can actually be approved with no
  // further input (manager stage) or needs more (HR stage, which needs an
  // asset + handover photo picked); either way clicking it does the right
  // thing, so no message-text sniffing needed here.
  //
  // entity_status (server-joined live against leave_requests/asset_requests,
  // not a static field on the notification row) gates both buttons on the
  // request still actually being Pending — previously these showed purely
  // from category+entity_id and relied on the backend to error out
  // gracefully if the notification was stale (already approved/rejected by
  // the time it's viewed), which is safe but still showed Approve/Reject on
  // an already-decided request, reading as broken even though a click
  // wouldn't have corrupted anything.
  const showQuickApprove = n.category === 'asset' && n.entity_id && n.entity_status === 'Pending' && onQuickApprove;
  // Leave requests get inline Approve/Reject — unlike assets, leave approval
  // always requires a comment (server-enforced), so these prompt for one
  // right here instead of a silent one-click, then call the same endpoint
  // the full Leaves page uses.
  const showLeaveActions = n.category === 'leave' && n.entity_id && n.entity_status === 'Pending' && onLeaveAction;
  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onNotifClick?.(n) : undefined}
      className={`w-full flex gap-[10px] ${dense ? "py-[10px] px-2 rounded-xl" : "py-3 px-2 rounded-lg"} border-b border-b0 last:border-0 text-left ${isRead ? "bg-transparent" : "bg-pg"} ${clickable ? 'cursor-pointer hover:bg-bg3' : 'cursor-default'}`}
    >
      <div className="flex-shrink-0 mt-[1px] w-8 h-8 rounded-[10px] flex items-center justify-center" style={{ background: `${c}18`, border: `1px solid ${c}33` }}>
        <Icon size={15} color={c} />
      </div>
      <div className="min-w-0 flex-1">
        <div className={`${dense ? "text-[14px] leading-[1.35]" : "text-[15px] leading-[1.4]"} ${isRead ? "text-t2" : "text-t0 font-semibold"}`}>{n.msg || n.message || ""}</div>
        <div className="flex items-center justify-between gap-2 mt-[4px] flex-wrap">
          <div className="text-[12px] text-t3 flex items-center gap-[6px]">
            <span>{fmtTime(n.t || n.time_ago)}</span>
            <span className="w-[3px] h-[3px] rounded-full bg-b1" />
            <span style={{ color: c }} className="font-semibold">{labelFor(n.category)}</span>
          </div>
          {showQuickApprove && (
            <button
              onClick={(e) => { e.stopPropagation(); onQuickApprove(n); }}
              className="text-[12px] font-bold px-[10px] py-[4px] rounded-lg border-0 cursor-pointer"
              style={{ background: K.g, color: '#fff' }}
            >Approve</button>
          )}
          {showLeaveActions && (
            <div className="flex gap-1">
              <button
                onClick={(e) => { e.stopPropagation(); onLeaveAction(n, 'approve'); }}
                className="text-[12px] font-bold px-[10px] py-[4px] rounded-lg border-0 cursor-pointer"
                style={{ background: K.g, color: '#fff' }}
              >Approve</button>
              <button
                onClick={(e) => { e.stopPropagation(); onLeaveAction(n, 'reject'); }}
                className="text-[12px] font-bold px-[10px] py-[4px] rounded-lg border-0 cursor-pointer"
                style={{ background: K.r, color: '#fff' }}
              >Reject</button>
            </div>
          )}
        </div>
      </div>
      {!isRead && <div className="w-[7px] h-[7px] rounded-full bg-p flex-shrink-0 mt-[11px]" />}
    </div>
  );
};

// Gmail-style full-window notification center — opened via the expand
// button in the header dropdown. Left rail: Inbox (unread), All, then one
// label per category actually present in this viewer's notifications (so
// an employee only ever sees labels relevant to them, an approver sees
// their approval-shaped ones, etc. — same hierarchy scoping the backend
// already applies to which notifications exist for them at all).
const NotificationCenter = ({ notifications, onClose, onNotifClick, onQuickApprove, onLeaveAction, onMarkAllRead }) => {
  const [tab, setTab] = useState('inbox');
  const unread = notifications.filter(n => !(n.is_read || n.rd));
  const categories = Array.from(new Set(notifications.map(n => n.category).filter(Boolean)));
  const countFor = (cat) => notifications.filter(n => n.category === cat).length;
  const visible = tab === 'inbox' ? unread : tab === 'all' ? notifications : notifications.filter(n => n.category === tab);

  const navBtn = (active, key, label, count, Ic, color) => (
    <button
      key={key}
      onClick={() => setTab(key)}
      className={`flex items-center gap-2 w-full px-[10px] py-[8px] rounded-[9px] border-0 text-left text-[14px] cursor-pointer transition-colors ${active ? "bg-pg text-p font-bold" : "bg-transparent text-t2 font-medium hover:bg-bg3 hover:text-t0"}`}
    >
      <Ic size={15} color={active ? K.p : color} className="flex-shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      {count > 0 && <span className="text-[12px] font-bold text-t3">{count}</span>}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[300] animate-fadeIn bg-black/40 flex items-center justify-center p-4" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl w-full h-full max-w-[1400px] max-h-[92vh] flex overflow-hidden shadow-[0_24px_70px_rgba(0,0,0,0.25)]" onMouseDown={e => e.stopPropagation()}>
        <div className="w-[220px] min-w-[220px] border-r border-b0 bg-bg2 p-3 flex flex-col overflow-y-auto">
          <div className="flex items-center justify-between mb-3 px-[2px]">
            <span className="text-[17px] font-extrabold text-t0">Notifications</span>
          </div>
          <div className="flex flex-col gap-[2px]">
            {navBtn(tab === 'inbox', 'inbox', 'Inbox', unread.length, Bell, K.bl)}
            {navBtn(tab === 'all', 'all', 'All', notifications.length, Inbox, K.t3)}
          </div>
          {categories.length > 0 && (
            <>
              <div className="text-[11px] font-bold uppercase tracking-wide text-t3 px-[10px] mt-3 mb-1">Labels</div>
              <div className="flex flex-col gap-[2px]">
                {categories.map(cat => navBtn(tab === cat, cat, labelFor(cat), countFor(cat), iconFor(cat).Icon, iconFor(cat).c))}
              </div>
            </>
          )}
          <div className="mt-auto pt-3">
            {unread.length > 0 && onMarkAllRead && (
              <Bt v="secondary" s="sm" icon={CheckCheck} full onClick={onMarkAllRead}>Mark all read</Bt>
            )}
          </div>
        </div>
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-b0 flex-shrink-0">
            <span className="text-[15px] font-bold text-t0">{tab === 'inbox' ? 'Inbox' : tab === 'all' ? 'All notifications' : labelFor(tab)}</span>
            <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-4">
            {visible.length === 0 && <div className="text-[14px] text-t3 text-center py-16">Nothing here.</div>}
            {visible.map((n, idx) => (
              <NotificationRow key={n.id || idx} n={n} onNotifClick={(x) => { onNotifClick?.(x); onClose(); }} onQuickApprove={onQuickApprove} onLeaveAction={onLeaveAction} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const EmployeeQuickView = ({ emp, onClose, role, onGoToProfile }) => (
  <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
    <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl w-full max-w-[628px] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.18)]" onMouseDown={e => e.stopPropagation()}>
      <div className="relative p-5 flex items-center gap-3" style={{ background: `linear-gradient(120deg, ${K.p}, #1E3A8A)` }}>
        <Av nm={emp.name} sz={52} rn="#ffffff" src={emp.photo} />
        <div className="min-w-0">
          <div className="text-white text-[16px] font-extrabold truncate">{emp.name}</div>
          <div className="text-white/85 text-[14px] font-medium">{emp.employee_id}</div>
        </div>
        <button onClick={onClose} className="absolute top-3 right-3 text-white bg-white/20 hover:bg-white/30 transition-colors rounded-lg p-1 border-0 cursor-pointer">
          <X size={15} />
        </button>
      </div>
      <div className="px-4 py-2">
        <Row label="Gender" value={emp.gender} />
        <Row label="Company Email" value={emp.email} />
        <Row label="Role" value={emp.role} />
        <Row label="Reporting Manager" value={emp.manager} />
        <Row label="Work Location" value={emp.location} />
        <Row label="Company" value={emp.company} />
      </div>
      {CAN_OPEN_PROFILE.includes(role) && onGoToProfile && (
        <div className="px-4 pb-4">
          <Bt icon={UserCog} full onClick={() => { onGoToProfile(emp.employee_id); onClose(); }}>Go to Profile</Bt>
        </div>
      )}
    </div>
  </div>
);

export const Header = ({ companies, selCompany, setSelCompany, showN, setShowN, nfRead, setNfRead, notifications, unread, onNotifClick, onMarkAllRead, onRefreshNotifications, onQuickApprove, onLeaveAction, userName, userPhoto, roleLabel, role, canPickCompany = false, onGoToMyProfile, onGoToProfile }) => {
  // The company picker is an HR/Admin scope tool. The employee search, however,
  // is now a company-directory lookup available to EVERYONE.
  const isAdminOrHr = !!canPickCompany; // Settings → Permissions › Employees › Basic Info (Edit)

  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null); // picked employee -> quick-view modal
  const [showCenter, setShowCenter] = useState(false); // Gmail-style full notification view
  const boxRef = useRef(null);

  // Debounced directory search — fires once typing settles (250ms) and 2+ chars.
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); setOpen(false); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get('/api/employees/directory?q=' + encodeURIComponent(q.trim()))
        .then(d => { setResults(d?.data || []); setOpen(true); })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // Close the dropdown when clicking outside the search box.
  useEffect(() => {
    const onDoc = e => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (emp) => { setSel(emp); setOpen(false); setQ(""); setResults([]); };

  return (
    <header className="relative h-[60px] px-3 sm:px-[22px] flex items-center justify-between gap-3 bg-bg1 flex-shrink-0 shadow-[0_1px_8px_rgba(15,23,42,0.05)] z-30">
      <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ background: "linear-gradient(90deg, #0a2364 0%, #0b4d79 55%, #059669 100%)" }} />
      <div ref={boxRef} className="relative hidden sm:block">
        <Inp
          icon={Search}
          placeholder="Search employees..."
          value={q}
          onChange={setQ}
          style={{ width: "clamp(180px, 24vw, 280px)" }}
          bg="bg-pb"
        />
        {open && (
          <div className="absolute top-[calc(100%+4px)] left-0 w-[300px] animate-dropIn bg-bg2 border border-b1 rounded-xl p-1 z-[100] max-h-[340px] overflow-auto shadow-[0_12px_36px_rgba(0,0,0,0.12)]">
            {loading && <div className="px-3 py-3 text-[14px] text-t3">Searching…</div>}
            {!loading && results.length === 0 && <div className="px-3 py-3 text-[14px] text-t3">No employees found</div>}
            {!loading && results.map(emp => (
              <button
                key={emp.employee_id}
                onClick={() => pick(emp)}
                className="w-full flex items-center gap-2 px-2 py-[6px] rounded-lg hover:bg-bg3 text-left border-0 bg-transparent cursor-pointer"
              >
                <Av nm={emp.name} sz={28} rn={K.p} src={emp.photo} />
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-semibold text-t0 truncate">{emp.name}</div>
                  <div className="text-[12px] text-t3 truncate">{emp.employee_id}{emp.role ? " · " + emp.role : ""}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {isAdminOrHr && (
        <div className="sm:ml-0">
          <Sl
            options={[{ v: "all", l: "All Companies" }, ...companies.map(c => ({ v: String(c.id), l: c.name }))]}
            value={selCompany}
            onChange={v => setSelCompany(v)}
            bg="bg-pb"
          />
        </div>
      )}

      <div className="flex items-center gap-2 ml-auto">
        <div className="relative">
          <button
            onClick={() => { setShowN(!showN); if (!showN) setNfRead(true); }}
            className="relative bg-pb border border-transparent rounded-xl p-[10px] cursor-pointer text-p flex hover:bg-[rgba(37,99,235,0.22)] transition-colors"
          >
            <Bell size={17} />
            {unread > 0 && (
              <span className="absolute -top-[3px] -right-[3px] min-w-[17px] h-[17px] px-[3px] rounded-full bg-r text-white text-[12px] font-bold flex items-center justify-center border-2 border-bg1">{unread}</span>
            )}
          </button>
          {showN && (() => {
            const unreadCount = notifications.filter(n => !(n.is_read || n.rd)).length;
            return (
            <div className="absolute top-[calc(100%+8px)] right-0 animate-dropIn bg-bg1 border border-b0 rounded-2xl z-[100] w-[380px] max-w-[92vw] shadow-[0_18px_48px_rgba(15,23,42,0.18)] overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-b0">
                <div className="flex items-center gap-2">
                  <span className="text-[15px] font-extrabold text-t0">Notifications</span>
                  {unreadCount > 0 && (
                    <span className="text-[11px] font-bold px-[8px] py-[2px] rounded-full" style={{ background: K.pg, color: K.p }}>{unreadCount} new</span>
                  )}
                </div>
                {unreadCount > 0 && onMarkAllRead && (
                  <button
                    onClick={() => onMarkAllRead()}
                    className="text-[12px] text-p font-semibold flex items-center gap-1 bg-transparent border-0 cursor-pointer hover:underline p-0"
                  >
                    <CheckCheck size={12} /> Mark all read
                  </button>
                )}
              </div>

              <div className="max-h-[380px] overflow-auto px-2 py-1">
                {notifications.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-2" style={{ background: K.pg }}>
                      <Bell size={20} color={K.p} />
                    </div>
                    <div className="text-[14px] font-bold text-t2">You're all caught up</div>
                    <div className="text-[12px] text-t3 mt-[2px]">Nothing new to read right now.</div>
                  </div>
                )}
                {notifications.map((n, idx) => (
                  <NotificationRow key={n.id || idx} n={n} onNotifClick={onNotifClick} onQuickApprove={onQuickApprove} onLeaveAction={onLeaveAction} dense />
                ))}
              </div>

              {notifications.length > 0 && (
                <button
                  onClick={() => { setShowCenter(true); setShowN(false); }}
                  className="w-full flex items-center justify-center gap-[6px] px-4 py-[11px] border-0 border-t border-b0 bg-transparent text-p text-[13px] font-bold cursor-pointer hover:bg-pg transition-colors"
                >
                  <Maximize2 size={13} /> View all notifications
                </button>
              )}
            </div>
            );
          })()}
          {showCenter && (
            <NotificationCenter
              notifications={notifications}
              onClose={() => setShowCenter(false)}
              onNotifClick={onNotifClick}
              onQuickApprove={onQuickApprove}
              onLeaveAction={onLeaveAction}
              onMarkAllRead={onMarkAllRead}
            />
          )}
        </div>

        <button
          onClick={onGoToMyProfile}
          className="flex items-center gap-[8px] pl-[6px] pr-[10px] py-[4px] rounded-xl border border-transparent bg-pb cursor-pointer hover:bg-[rgba(37,99,235,0.22)] transition-colors"
        >
          <Av nm={userName} sz={28} rn={K.p} src={userPhoto} />
          <div className="hidden md:block text-left">
            <div className="text-[13px] font-bold text-t0 truncate max-w-[140px] leading-tight">{userName}</div>
            {roleLabel && <div className="mt-[1px]"><Bg sm>{roleLabel}</Bg></div>}
          </div>
        </button>
      </div>

      {sel && <EmployeeQuickView emp={sel} onClose={() => setSel(null)} role={role} onGoToProfile={onGoToProfile} />}
    </header>
  );
};
